import React, { useCallback, useEffect, useState } from 'react';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  strapiDownloadWorkOrderPermohonanPengujianPdf,
  strapiDownloadWorkOrderRpsPdf,
  strapiOpenWorkOrderPermohonanPengujianPdfHtmlPreview,
  strapiOpenWorkOrderRpsPdfHtmlPreview,
} from '../../../api/strapiCrm';

type PdfKind = 'rps' | 'permohonan';

type Props = {
  open: boolean;
  workOrderDocumentId: string | null;
  workOrderLabel?: string | null;
  jwt: string | null;
  /** Bust cache so SPV-signed RPS appears after approve. */
  forceFresh?: boolean;
  onClose: () => void;
};

function revokeUrl(url: string | null): void {
  if (url) URL.revokeObjectURL(url);
}

function downloadBlobUrl(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function SamplePreservationCombinedPdfPreviewDialog({
  open,
  workOrderDocumentId,
  workOrderLabel,
  jwt,
  forceFresh = false,
  onClose,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [rpsUrl, setRpsUrl] = useState<string | null>(null);
  const [permohonanUrl, setPermohonanUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [openingHtml, setOpeningHtml] = useState<PdfKind | null>(null);
  const [activeTab, setActiveTab] = useState<PdfKind>('rps');

  const loadPdfs = useCallback(
    async (opts?: { keepPreview?: boolean }) => {
      if (!workOrderDocumentId || !jwt) return;
      setLoading(true);
      setError(null);
      if (!opts?.keepPreview) {
        setRpsUrl((prev) => {
          revokeUrl(prev);
          return null;
        });
        setPermohonanUrl((prev) => {
          revokeUrl(prev);
          return null;
        });
      }
      try {
        const cacheBust = Boolean(opts?.keepPreview || forceFresh);
        const [rpsBlob, permohonanBlob] = await Promise.all([
          strapiDownloadWorkOrderRpsPdf(jwt, workOrderDocumentId, { cacheBust }),
          strapiDownloadWorkOrderPermohonanPengujianPdf(jwt, workOrderDocumentId, { cacheBust }),
        ]);
        setRpsUrl((prev) => {
          revokeUrl(prev);
          return URL.createObjectURL(rpsBlob);
        });
        setPermohonanUrl((prev) => {
          revokeUrl(prev);
          return URL.createObjectURL(permohonanBlob);
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Gagal memuat PDF RPS dan Permohonan Pengujian.');
      } finally {
        setLoading(false);
      }
    },
    [jwt, workOrderDocumentId, forceFresh],
  );

  useEffect(() => {
    if (!open || !workOrderDocumentId || !jwt) return undefined;
    setActiveTab('rps');
    void loadPdfs();
    return undefined;
  }, [open, workOrderDocumentId, jwt, loadPdfs]);

  useEffect(() => {
    if (open) return undefined;
    setRpsUrl((prev) => {
      revokeUrl(prev);
      return null;
    });
    setPermohonanUrl((prev) => {
      revokeUrl(prev);
      return null;
    });
    setError(null);
    setLoading(false);
    setRegenerating(false);
    setOpeningHtml(null);
    setActiveTab('rps');
    return undefined;
  }, [open]);

  async function handleRegenerate() {
    if (!workOrderDocumentId || !jwt || loading || regenerating || openingHtml) return;
    setRegenerating(true);
    try {
      await loadPdfs({ keepPreview: true });
    } finally {
      setRegenerating(false);
    }
  }

  async function handleOpenHtmlPreview(kind: PdfKind) {
    if (!workOrderDocumentId || !jwt || loading || regenerating || openingHtml) return;
    setOpeningHtml(kind);
    setError(null);
    try {
      if (kind === 'rps') {
        await strapiOpenWorkOrderRpsPdfHtmlPreview(jwt, workOrderDocumentId);
      } else {
        await strapiOpenWorkOrderPermohonanPengujianPdfHtmlPreview(jwt, workOrderDocumentId);
      }
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : kind === 'rps'
            ? 'Gagal membuka preview HTML RPS.'
            : 'Gagal membuka preview HTML Permohonan Pengujian.',
      );
    } finally {
      setOpeningHtml(null);
    }
  }

  function handleDownload(kind: PdfKind) {
    const url = kind === 'rps' ? rpsUrl : permohonanUrl;
    if (!url) return;
    const safe = (workOrderLabel?.trim() || workOrderDocumentId || 'dokumen').replace(/[^\w.-]+/g, '_');
    const filename =
      kind === 'rps' ? `RPS_${safe}.pdf` : `PERMOHONAN_PENGUJIAN_${safe}.pdf`;
    downloadBlobUrl(url, filename);
  }

  const titleLabel = workOrderLabel?.trim() || workOrderDocumentId || 'dokumen';
  const pdfBusy = loading || regenerating;
  const busy = pdfBusy || Boolean(openingHtml);
  const hasBoth = Boolean(rpsUrl && permohonanUrl);
  const activeUrl = activeTab === 'rps' ? rpsUrl : permohonanUrl;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="sample-preservation-combined-pdf-preview-title"
      data-testid="sample-preservation-combined-pdf-preview-dialog"
    >
      <DialogTitle id="sample-preservation-combined-pdf-preview-title" sx={{ pr: 6 }}>
        PDF RPS & Permohonan Pengujian — {titleLabel}
        <IconButton
          aria-label="Tutup"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          height: { xs: '70vh', md: '80vh' },
          bgcolor: 'grey.100',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}
        {!error && pdfBusy && !hasBoth ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 2, p: 3 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {regenerating
                ? 'Menggenerate ulang PDF RPS dan Permohonan Pengujian…'
                : 'Menyiapkan PDF RPS dan Permohonan Pengujian…'}
            </Typography>
          </Stack>
        ) : null}
        {!error && hasBoth ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v: PdfKind) => setActiveTab(v)}
              sx={{ px: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="RPS" value="rps" data-testid="sample-preservation-combined-pdf-tab-rps" />
              <Tab
                label="Permohonan Pengujian"
                value="permohonan"
                data-testid="sample-preservation-combined-pdf-tab-permohonan"
              />
            </Tabs>
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, p: '16px' }}>
              {pdfBusy ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 2,
                    bgcolor: 'rgba(255,255,255,0.72)',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Menggenerate ulang PDF RPS dan Permohonan Pengujian…
                  </Typography>
                </Stack>
              ) : null}
              <Box
                component="iframe"
                src={activeUrl ?? undefined}
                title={
                  activeTab === 'rps'
                    ? `PDF RPS — ${titleLabel}`
                    : `PDF Permohonan Pengujian — ${titleLabel}`
                }
                sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={() => void handleRegenerate()}
          disabled={!workOrderDocumentId || !jwt || busy}
          startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <AutorenewOutlinedIcon />}
          data-testid="sample-preservation-combined-pdf-regenerate"
        >
          Generate ulang
        </Button>
        <Button
          onClick={() => void handleOpenHtmlPreview('rps')}
          disabled={!workOrderDocumentId || !jwt || busy}
          startIcon={openingHtml === 'rps' ? <CircularProgress size={16} color="inherit" /> : <OpenInNewOutlinedIcon />}
          data-testid="sample-preservation-combined-pdf-html-rps"
        >
          Preview HTML RPS
        </Button>
        <Button
          onClick={() => void handleOpenHtmlPreview('permohonan')}
          disabled={!workOrderDocumentId || !jwt || busy}
          startIcon={
            openingHtml === 'permohonan' ? <CircularProgress size={16} color="inherit" /> : <OpenInNewOutlinedIcon />
          }
          data-testid="sample-preservation-combined-pdf-html-permohonan"
        >
          Preview HTML Permohonan
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => handleDownload('rps')}
          disabled={!rpsUrl || busy}
          startIcon={<DownloadOutlinedIcon />}
          data-testid="sample-preservation-combined-pdf-download-rps"
        >
          Unduh RPS
        </Button>
        <Button
          onClick={() => handleDownload('permohonan')}
          disabled={!permohonanUrl || busy}
          startIcon={<DownloadOutlinedIcon />}
          data-testid="sample-preservation-combined-pdf-download-permohonan"
        >
          Unduh PP
        </Button>
        <Button onClick={onClose} disabled={busy}>
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
}
